export interface HandoffNavigationInput {
  identificador_rota: string
  rota_autorizada: string
  rascunho_opaco?: string | null
}

export interface HandoffMatchedRouteRecord {
  path?: unknown
  redirect?: unknown
}

export interface HandoffResolvedRoute {
  path: string
  fullPath?: string
  matched: readonly HandoffMatchedRouteRecord[]
}

export type HandoffRouteResolver = (location: string) => HandoffResolvedRoute

export interface HandoffNavigationTarget {
  path: string
  state: {
    identificador_rota: string
    rascunho_opaco?: string | null
  }
}

export type HandoffNavigationFailureCode =
  | 'dados_invalidos'
  | 'rota_externa'
  | 'rota_nao_registrada'
  | 'rota_inconsistente'
  | 'falha_ao_resolver_rota'
  | 'falha_ao_navegar'

export type HandoffNavigationResult =
  | { ok: true, target: HandoffNavigationTarget }
  | { ok: false, code: HandoffNavigationFailureCode }

const PROTOCOLO_URL = /^[a-z][a-z\d+.-]*:/i
const ROTA_CATCH_ALL = /^\/:pathMatch\(\.\*\)\*\/?$/

function textoNaoVazio (valor: unknown): valor is string {
  return typeof valor === 'string' && valor.trim().length > 0
}

function rotaLocalSegura (rota: unknown): rota is string {
  if (!textoNaoVazio(rota)) return false

  return rota.startsWith('/') && !rota.startsWith('//') && !PROTOCOLO_URL.test(rota)
}

function recordConcreto (record: unknown): record is HandoffMatchedRouteRecord {
  if (typeof record !== 'object' || record === null) return false

  const rota = record as HandoffMatchedRouteRecord
  return textoNaoVazio(rota.path) &&
    !ROTA_CATCH_ALL.test(rota.path) &&
    rota.redirect === undefined
}

function resolucaoValida (resolucao: unknown): resolucao is HandoffResolvedRoute {
  if (typeof resolucao !== 'object' || resolucao === null) return false

  const rota = resolucao as HandoffResolvedRoute
  return textoNaoVazio(rota.path) &&
    (rota.fullPath === undefined || textoNaoVazio(rota.fullPath)) &&
    Array.isArray(rota.matched)
}

export function prepararNavegacaoHandoff (
  handoff: HandoffNavigationInput,
  resolver: HandoffRouteResolver
): HandoffNavigationResult {
  if (!textoNaoVazio(handoff?.identificador_rota) || !textoNaoVazio(handoff?.rota_autorizada)) {
    return { ok: false, code: 'dados_invalidos' }
  }

  if (!rotaLocalSegura(handoff.rota_autorizada)) {
    return { ok: false, code: 'rota_externa' }
  }

  let resolvida: unknown
  try {
    resolvida = resolver(handoff.rota_autorizada)
  } catch {
    return { ok: false, code: 'falha_ao_resolver_rota' }
  }

  if (!resolucaoValida(resolvida) || !resolvida.matched.some(recordConcreto)) {
    return { ok: false, code: 'rota_nao_registrada' }
  }

  // Vue Router separa a query do path; o handoff transporta a URL completa.
  const rotaCompleta = resolvida.fullPath ?? resolvida.path
  if (rotaCompleta !== handoff.rota_autorizada) {
    return { ok: false, code: 'rota_inconsistente' }
  }

  return {
    ok: true,
    target: {
      path: rotaCompleta,
      state: {
        identificador_rota: handoff.identificador_rota,
        rascunho_opaco: handoff.rascunho_opaco
      }
    }
  }
}

export type HandoffNavigationPush = (target: HandoffNavigationTarget) => Promise<unknown>

export type HandoffPushResult =
  | { ok: true }
  | { ok: false, code: 'falha_ao_navegar' }

export async function executarNavegacaoHandoff (
  target: HandoffNavigationTarget,
  push: HandoffNavigationPush
): Promise<HandoffPushResult> {
  try {
    const falha = await push(target)
    return falha ? { ok: false, code: 'falha_ao_navegar' } : { ok: true }
  } catch {
    return { ok: false, code: 'falha_ao_navegar' }
  }
}
